/*************************************************
 * MaxCap
 * @exports
 * AuthApi.ts
 * Created by vinoth kumar on  15/05/2024
 * Copyright © 2024 MaxCap. All rights reserved.
 *************************************************/

import { createApi } from "@reduxjs/toolkit/query/react";
import CustomFetchBase from "./CustomFetchBase";
import { setUser, setImpersonateUser } from "../slice/UserSlice";

export const authApi = createApi({
    reducerPath: "authApi",
    baseQuery: CustomFetchBase,
    endpoints: (builder) => ({
        loginUser: builder.mutation({
            query: (body) => ({
                url: `login`,
                method: "POST",
                body,
            }),
            onQueryStarted: async (args, {dispatch, queryFulfilled}) => {
                try {
                    const {data} = await queryFulfilled;
                    if (data.data?.users?.is_verified) {
                        //dispatch(setUser([]));
                        dispatch(setUser(data.data?.users));
                    }
                } catch (error) {
                }
            },
        }),
        googleAuth: builder.mutation({
            query: (body) => ({
                url: `google-auth`,
                method: "POST",
                body,
            }),
        }),
        logoutUser: builder.mutation({
            query: (body) => ({
                url: `logout`,
                method: "POST",
                body,
            }),
        }),
        forgotPassword: builder.mutation({
            query: (body) => ({
                url: `send-forgot-password-otp`,
                method: "POST",
                body,
            }),
        }),
        changePassword: builder.mutation({
            query: (body) => ({
                url: `rest-password`,
                method: "POST",
                body,
            }),
        }),
        registerUser: builder.mutation({
            query: (body) => ({
                url: `register`,
                method: "POST",
                body,
            }),
        }),
        lookupUser: builder.mutation({
            query: (body) => ({
                url: `lookup-user`,
                method: "POST",
                body,
            }),
        }),
        verifyEmail: builder.mutation({
            query: (body) => ({
                url: `verify-email`,
                method: "POST",
                body,
            }),
            onQueryStarted: async (args, {dispatch, queryFulfilled}) => {
                try {
                    const {data} = await queryFulfilled;
                    if (data.data?.users?.is_verified) {
                        dispatch(setUser(data.data?.users));
                    }
                } catch (error) {
                }
            },
        }),
        rensendVerifyEmail: builder.mutation({
            query: (body) => ({
                url: `resend-verify-email`,
                method: "POST",
                body,
            }),
        }),
        getDashboardDetails: builder.query({
            query: () => ({
                url: `dashboard`,
                method: "GET",
            }),
        }),
        requestPayoutLater: builder.mutation({
            query: (body) => ({
                url: `request-payoff-letter`,
                method: "POST",
                body,
            }),
        }),
        // viewStatement: builder.mutation({
        //   query: (body) => ({
        //     url: `download-statement`,
        //     method: "POST",
        //     body,
        //   }),
        // }),
        viewStatement: builder.mutation<Blob, { opportunity_number: string }>({
            query: (body) => ({
                url: "download-statement",
                method: "POST",
                body,
                headers: {
                    "Content-Type": "application/json",
                },
                responseHandler: (response: Response) => response.blob(),
                cache: "no-store",
            }),
        }),
        requestFunding: builder.mutation({
            query: (body) => ({
                url: `request-funding`,
                method: "POST",
                body,
            }),
        }),
        getImpersonateUser: builder.query({
            query: (params) => ({
                url: `impersonate-signin`,
                method: "GET",
                params,
            }),
            onQueryStarted: async (args, {dispatch, queryFulfilled}) => {
                try {
                    const {data} = await queryFulfilled;
                    if (data.data?.users?.is_verified) {
                        dispatch(setUser(data.data?.users));
                        dispatch(setImpersonateUser());

                    }
                } catch (error) {
                }
            },
        }),
        lazyGetAppFormDetails: builder.mutation({
            query: (body) => ({
                url: `get-application-form-data`,
                method: "POST",
                body,
            }),
        }),
        submitAppForm: builder.mutation({
            query: (body) => ({
                url: `submit-application-form`,
                method: "POST",
                body,
            }),
        }),
        updateRenewalMailingAddress: builder.mutation({
            query: (body) => ({
                url: `update-renewal-mailing-address`,
                method: "POST",
                body,
            }),
        }),
        getCanSubmitNewApp: builder.mutation({
            query: () => ({
                url: `can-submit-new-application`,
                method: "POST",
            }),
        }),
        documentsUpload: builder.mutation({
            query: (body) => ({
                url: `file-upload`,
                method: "POST",
                body,
            }),
        }),
        getUsersList: builder.query({
            query: ({currentPage, searchQuery}) => {
                return {
                    url: `users?page=${currentPage}${searchQuery}`,
                    method: "GET",
                };
            },
        }),
        getUserDetails: builder.query({
            query: ({userId}) => ({
                url: `users/${userId}`,
                method: "GET",
            }),
        }),
        updateUser: builder.mutation({
            query: (args) => {
                const {userId, body} = args;
                return {
                    url: `/users/${userId}`,
                    method: "PUT",
                    body,
                };
            }
        }),
        activateUser: builder.mutation({
            query: ({userId}) => ({
                url: `user/${userId}/activate`,
                method: "POST",
            }),
        }),
        deactivateUser: builder.mutation({
            query: ({userId}) => ({
                url: `user/${userId}/deactivate`,
                method: "POST",
            }),
        }),
        deleteUser: builder.mutation({
            query: ({userId}) => ({
                url: `users/${userId}`,
                method: "DELETE",
            }),
        }),
        getUsersDataCSV: builder.query({
            query: () => ({
                url: `/users/export`,
                method: "GET",
            }),
        }),
        sendPlaidJourney: builder.mutation({
            query: (body) => {
                return {
                    url: `plaid/journey`,
                    method: "POST",
                    body,
                };
            }
        }),
        sendPlaidExchange: builder.mutation({
            query: (body) => {
                return {
                    url: `plaid/exchange`,
                    method: "POST",
                    body,
                };
            }
        }),
        getOrderDetails: builder.query({
            query: (opportunityNumber: string) => ({
                url: `order/${opportunityNumber}`,
                method: "GET",
            }),
        }),
        downloadLoanAgreement: builder.mutation({
            query: (body) => ({
                url: `download-loan-agreement`,
                method: "POST",
                body,
                headers: {
                    "Content-Type": "application/json",
                },
                responseHandler: async (response: Response) => {
                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }

                    const blob = await response.blob();
                    const arrayBuffer = await blob.arrayBuffer();
                    const uint8Array = new Uint8Array(arrayBuffer);

                    const pdfSignature = [0x25, 0x50, 0x44, 0x46];
                    const isValidPDF = pdfSignature.every((byte, index) => uint8Array[index] === byte);

                    if (!isValidPDF) {
                        const text = new TextDecoder().decode(uint8Array);
                        console.log('Invalid PDF response:', text);
                        throw new Error('Document not found or server returned an error page');
                    }

                    const url = window.URL.createObjectURL(blob);
                    window.open(url, '_blank');

                    setTimeout(() => {
                        window.URL.revokeObjectURL(url);
                    }, 100);

                    return {success: true, opened: true};
                },
            }),
        }),
        sendRenewalEmail: builder.mutation({
            query: () => {
                return {
                    url: `send-email-for-renewal`,
                    method: "POST",
                };
            }
        }),
        sendRequestSettlement: builder.mutation({
            query: (formData: FormData) => ({
                url: `/request-settlement`,
                method: "POST",
                body: formData,
            }),
        }),
    })
})

export const {
  useLoginUserMutation,
  useGoogleAuthMutation,
  useLogoutUserMutation,
  useForgotPasswordMutation,
  useChangePasswordMutation,
  useRegisterUserMutation,
  useLookupUserMutation,
  useVerifyEmailMutation,
  useRensendVerifyEmailMutation,
  useLazyGetDashboardDetailsQuery,
  useViewStatementMutation,
  useRequestPayoutLaterMutation,
  useRequestFundingMutation,
  useLazyGetImpersonateUserQuery,
  useLazyGetAppFormDetailsMutation,
  useSubmitAppFormMutation,
  useUpdateRenewalMailingAddressMutation,
  useGetCanSubmitNewAppMutation,
  useDocumentsUploadMutation,
  useLazyGetUsersListQuery,
  useLazyGetUserDetailsQuery,
  useUpdateUserMutation,
  useActivateUserMutation,
  useDeactivateUserMutation,
  useDeleteUserMutation,
  useLazyGetUsersDataCSVQuery,
  useSendPlaidJourneyMutation,
  useSendPlaidExchangeMutation,
  useDownloadLoanAgreementMutation,
  useSendRenewalEmailMutation,
  useSendRequestSettlementMutation,
  useLazyGetOrderDetailsQuery
} = authApi;
